import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import type { Unsubscribe } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { PluginListenerHandle } from '@capacitor/core'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import type { RegistrationError, Token } from '@capacitor/push-notifications'
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings'
import { saveFCMToken } from '@/lib/firebase'

/**
 * Get whether the OS has granted notification permission (native iOS / Android only).
 */
export async function getNativeNotificationSettings(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false
  const platform = Capacitor.getPlatform()
  if (platform !== 'ios' && platform !== 'android') return false

  try {
    const permStatus = await PushNotifications.checkPermissions()
    return permStatus.receive === 'granted'
  } catch (error) {
    console.error('[Notifications] Error checking native notification permissions:', error)
    return false
  }
}

/**
 * Sync OS notification permission into Firestore (`ios` or `android` subtree).
 */
export async function syncDeviceNotificationStateToFirestore(userId: string): Promise<void> {
  if (!userId || !Capacitor.isNativePlatform()) return
  const platform = Capacitor.getPlatform()
  if (platform !== 'ios' && platform !== 'android') return

  try {
    const nativeEnabled = await getNativeNotificationSettings()
    const patch =
      platform === 'ios'
        ? { notificationSettings: { ios: { enabled: nativeEnabled, lastUpdated: serverTimestamp() } } }
        : { notificationSettings: { android: { enabled: nativeEnabled, lastUpdated: serverTimestamp() } } }

    await setDoc(doc(db, 'users', userId), patch, { merge: true })
    console.log('[Notifications] Synced device notification availability to Firestore:', nativeEnabled)
  } catch (error) {
    console.error('[Notifications] Error syncing device notification state:', error)
  }
}

export function subscribeIosEnabled(userId: string, onChange: (enabled: boolean) => void): Unsubscribe {
  const ref = doc(db, 'users', userId)

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    syncDeviceNotificationStateToFirestore(userId).catch((error) => {
      console.error('[Notifications] Error syncing on subscribe:', error)
    })
  }

  return onSnapshot(ref, (snap) => {
    const data = snap.data() as any
    const firestoreEnabled = data?.notificationSettings?.ios?.enabled
    if (typeof firestoreEnabled !== 'boolean') return

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      PushNotifications.checkPermissions()
        .then((permStatus) => {
          const actuallyEnabled = firestoreEnabled && permStatus.receive === 'granted'
          const nativeStatus = permStatus.receive === 'granted' ? 'granted' : 'denied'
          updateNativeNotificationStatus(userId, nativeStatus).catch((error) => {
            console.error('[Notifications] Error updating native status:', error)
          })
          onChange(actuallyEnabled)
        })
        .catch((error) => {
          console.error('[Notifications] Error checking native permissions:', error)
          onChange(firestoreEnabled)
        })
    } else {
      onChange(firestoreEnabled)
    }
  })
}

/** Android app: subscribe to `notificationSettings.android` and merge OS permission when native. */
export function subscribeAndroidEnabled(userId: string, onChange: (enabled: boolean) => void): Unsubscribe {
  const ref = doc(db, 'users', userId)

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    syncDeviceNotificationStateToFirestore(userId).catch((error) => {
      console.error('[Notifications] Error syncing on Android subscribe:', error)
    })
  }

  return onSnapshot(ref, (snap) => {
    const data = snap.data() as any
    const firestoreEnabled = data?.notificationSettings?.android?.enabled
    if (typeof firestoreEnabled !== 'boolean') return

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      PushNotifications.checkPermissions()
        .then((permStatus) => {
          const actuallyEnabled = firestoreEnabled && permStatus.receive === 'granted'
          const nativeStatus = permStatus.receive === 'granted' ? 'granted' : 'denied'
          updateNativeNotificationStatus(userId, nativeStatus).catch((error) => {
            console.error('[Notifications] Error updating Android native status:', error)
          })
          onChange(actuallyEnabled)
        })
        .catch((error) => {
          console.error('[Notifications] Error checking Android permissions:', error)
          onChange(firestoreEnabled)
        })
    } else {
      onChange(firestoreEnabled)
    }
  })
}

export async function setIosEnabled(userId: string, enabled: boolean): Promise<void> {
  let actuallyEnabled = enabled

  if (enabled && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    try {
      const permStatus = await PushNotifications.checkPermissions()
      actuallyEnabled = permStatus.receive === 'granted'
    } catch (error) {
      console.error('[Notifications] Error checking permissions when setting iOS enabled:', error)
      actuallyEnabled = false
    }
  }

  await setDoc(
    doc(db, 'users', userId),
    { notificationSettings: { ios: { enabled: actuallyEnabled, lastUpdated: serverTimestamp() } } },
    { merge: true }
  )
}

export async function setAndroidEnabled(userId: string, enabled: boolean): Promise<void> {
  let actuallyEnabled = enabled

  if (enabled && Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    try {
      const permStatus = await PushNotifications.checkPermissions()
      actuallyEnabled = permStatus.receive === 'granted'
    } catch (error) {
      console.error('[Notifications] Error checking permissions when setting Android enabled:', error)
      actuallyEnabled = false
    }
  }

  await setDoc(
    doc(db, 'users', userId),
    { notificationSettings: { android: { enabled: actuallyEnabled, lastUpdated: serverTimestamp() } } },
    { merge: true }
  )
}

export async function updateIosFcmToken(userId: string, fcmToken: string): Promise<void> {
  await setDoc(
    doc(db, 'users', userId),
    { notificationSettings: { ios: { fcmToken, lastUpdated: serverTimestamp() } } },
    { merge: true }
  )
}

export async function updateAndroidFcmToken(userId: string, fcmToken: string): Promise<void> {
  await setDoc(
    doc(db, 'users', userId),
    { notificationSettings: { android: { fcmToken, lastUpdated: serverTimestamp() } } },
    { merge: true }
  )
}

export async function updateIosApnsToken(userId: string, apnsToken: string): Promise<void> {
  await setDoc(
    doc(db, 'users', userId),
    { notificationSettings: { ios: { apnsToken, lastUpdated: serverTimestamp() } } },
    { merge: true }
  )
}

export async function updateNativeNotificationStatus(userId: string, status: 'granted' | 'denied'): Promise<void> {
  if (!userId) return
  const platform = Capacitor.getPlatform()
  if (platform !== 'ios' && platform !== 'android') return

  try {
    const patch =
      platform === 'ios'
        ? { notificationSettings: { ios: { nativeStatus: status } } }
        : { notificationSettings: { android: { nativeStatus: status } } }

    await setDoc(doc(db, 'users', userId), patch, { merge: true })
    console.log('[Notifications] Updated native permission status in Firestore:', platform, status)
  } catch (error) {
    console.error('[Notifications] Error updating native status:', error)
  }
}

export async function openNotificationSystemSettings(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  const platform = Capacitor.getPlatform()

  try {
    if (platform === 'ios') {
      const result = await NativeSettings.openIOS({ option: IOSSettings.AppNotification })
      if (!result.status) console.error('[Notifications] Failed to open iOS notification settings')
    } else if (platform === 'android') {
      const result = await NativeSettings.openAndroid({ option: AndroidSettings.AppNotification })
      if (!result.status) console.error('[Notifications] Failed to open Android notification settings')
    }
  } catch (error) {
    console.error('[Notifications] Error opening system notification settings:', error)
  }
}

/** @deprecated Use openNotificationSystemSettings */
export async function openIosNotificationSettings(): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return
  await openNotificationSystemSettings()
}

export async function setIosTokenError(userId: string, error: any): Promise<void> {
  const message = typeof error === 'string' ? error : (error?.message || 'unknown_error')
  const code = (error?.code || error?.name || 'unknown')
  await setDoc(
    doc(db, 'users', userId),
    { notificationSettings: { ios: { lastError: { code, message, at: serverTimestamp() } } } },
    { merge: true }
  )
}

/**
 * Wait for one FCM registration result. Removes only the temporary listeners we add —
 * never call removeAllListeners() here (that breaks e.g. useDeepLink push tap handlers).
 */
function waitForNativePushRegistrationToken(timeoutMs: number): Promise<string | null> {
  return new Promise((resolve) => {
    let settled = false
    let regHandle: PluginListenerHandle | undefined
    let errHandle: PluginListenerHandle | undefined
    const timeoutId = window.setTimeout(() => void finish(null), timeoutMs)

    const cleanupHandles = async () => {
      try {
        await regHandle?.remove()
      } catch {
        /* noop */
      }
      try {
        await errHandle?.remove()
      } catch {
        /* noop */
      }
    }

    const finish = (token: string | null) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      void cleanupHandles().finally(() => resolve(token))
    }

    void (async () => {
      try {
        regHandle = await PushNotifications.addListener('registration', (token: Token) => {
          finish(token.value)
        })
        errHandle = await PushNotifications.addListener(
          'registrationError',
          (err: RegistrationError) => {
            console.warn('[Notifications] Push registrationError:', err?.error)
            finish(null)
          }
        )
        await PushNotifications.register()
      } catch (e) {
        console.warn('[Notifications] PushNotifications.register failed:', e)
        finish(null)
      }
    })()
  })
}

async function getExistingFcmTokenFromFirestore(userId: string, platform: 'ios' | 'android'): Promise<string | null> {
  try {
    const snap = await getDoc(doc(db, 'users', userId))
    const data = snap.data() as any
    if (platform === 'ios') {
      const fromIos = data?.notificationSettings?.ios?.fcmToken
      if (typeof fromIos === 'string' && fromIos.length > 0) return fromIos
      const root = data?.fcmToken
      if (typeof root === 'string' && root.length > 0) return root
    } else {
      const fromAndroid = data?.notificationSettings?.android?.fcmToken
      if (typeof fromAndroid === 'string' && fromAndroid.length > 0) return fromAndroid
    }
    return null
  } catch (error) {
    console.error('[Notifications] Error reading saved FCM token:', error)
    return null
  }
}

async function syncNativeFcmTokenFromPreferences(userId: string, platform: 'ios' | 'android'): Promise<boolean> {
  if (!userId) return false
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== platform) return false

  let permission = await PushNotifications.checkPermissions()

  if (permission.receive === 'denied') {
    await updateNativeNotificationStatus(userId, 'denied')
    if (platform === 'ios') await setIosEnabled(userId, false)
    else await setAndroidEnabled(userId, false)
    console.log('[Notifications] Permission denied at system level; user must enable in Settings')
    return false
  }

  if (permission.receive !== 'granted') {
    permission = await PushNotifications.requestPermissions()
  }

  if (permission.receive !== 'granted') {
    await updateNativeNotificationStatus(userId, 'denied')
    if (platform === 'ios') await setIosEnabled(userId, false)
    else await setAndroidEnabled(userId, false)
    console.log('[Notifications] User denied or dismissed notification permissions')
    return false
  }

  await updateNativeNotificationStatus(userId, 'granted')

  let receivedToken: string | null = await getExistingFcmTokenFromFirestore(userId, platform)

  if (!receivedToken) {
    const regTimeoutMs = platform === 'ios' ? 12000 : 8000
    receivedToken = await waitForNativePushRegistrationToken(regTimeoutMs)
  }

  if (!receivedToken) {
    receivedToken = await getExistingFcmTokenFromFirestore(userId, platform)
  }

  if (!receivedToken) {
    const nativeGranted = await getNativeNotificationSettings()
    if (nativeGranted) {
      if (platform === 'ios') await setIosEnabled(userId, true)
      else await setAndroidEnabled(userId, true)
      return true
    }
    return false
  }

  if (platform === 'ios') {
    await updateIosFcmToken(userId, receivedToken)
  } else {
    await updateAndroidFcmToken(userId, receivedToken)
  }
  await saveFCMToken(userId, receivedToken)
  if (platform === 'ios') await setIosEnabled(userId, true)
  else await setAndroidEnabled(userId, true)
  return true
}

export async function syncIosFcmTokenFromPreferences(userId: string): Promise<boolean> {
  return syncNativeFcmTokenFromPreferences(userId, 'ios')
}

export async function syncAndroidFcmTokenFromPreferences(userId: string): Promise<boolean> {
  return syncNativeFcmTokenFromPreferences(userId, 'android')
}

export async function cleanupIosOnSignOut(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  const platform = Capacitor.getPlatform()
  if (platform !== 'ios' && platform !== 'android') return

  try {
    await PushNotifications.removeAllDeliveredNotifications()
  } catch (error) {
    console.error('[Notifications] Error during native push cleanup on sign-out:', error)
  }
}
